import React, { useState, useEffect } from "react";
import { Box, TextField, Typography, Checkbox, FormControlLabel, Link } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useStoreState } from "easy-peasy";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import UserService from "../../../../../services/userService";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import { PRODUCT } from "../../../../../models/websiteTypesConstants";
import AdobeScript from "../../../../../services/adobeScript";

const useStyles = makeStyles({
  container: {
    maxWidth: 480,
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  },

  textField: (props) => ({
    width: "100%",

    "& .MuiInputBase-root": {
      width: 400,
      backgroundColor: "#fff",
    },

    "& .MuiOutlinedInput-root": {
      height: 40,
      borderRadius: 4,
      backgroundColor: "#fff",

      "& fieldset": {
        borderColor: "#D9DDE4",
      },

      "&:hover fieldset": {
        borderColor: "#D9DDE4",
      },

      "&.Mui-focused fieldset": {
        borderColor: props.brandColor,
      },
    },

    "& .MuiOutlinedInput-input": {
      padding: "8px 0",
      fontSize: 14,
      color: "#555",

      "&::placeholder": {
        color: "#B9C0CA",
        opacity: 1,
      },
    },

    "& .MuiInputAdornment-positionStart": {
      marginRight: 6,
    },

    "& .MuiInputAdornment-positionEnd": {
      marginLeft: 6,
    },
    "& .MuiOutlinedInput-adornedEnd": {
      paddingRight: "22px",
    },
    termsContainer: {
      marginTop: 12,
      width: "100%",
    },
    
    checkbox: {
      padding: 0,
      marginRight: 6,
      color: "#C8CDD4",
    
      "&.Mui-checked": {
        color: "#4B86C5",
      },
    },
    termsLink: (props) => ({
      color: props.secondaryColor,
      textDecoration: "none",
    
      "&:hover": {
        textDecoration: "underline",
      },
    }),
  }),
  lockIcon: (props) => ({
    fontSize: 14,
    color: props.secondaryColor,
  }),
  visibilityButton: {
    padding: 2,
  },

  visibilityIcon: {
    fontSize: 14,
    color: "#C4C9D1",
  },

  rulesWrap: {
    marginTop: 10,
    width: "100%",
    textAlign: "left",
    fontSize: 12,
  },

  ruleRow: {
    display: "flex",
    alignItems: "center",
    marginTop: 1,
  },
});

export default function CreatePassword({ onValidityChange }) {
  const userService = UserService();
  const adobe = new AdobeScript();
  const brandSettings = useStoreState((s) => s.siteSettingsModel.brandSettings);
  const brandColor = brandSettings?.FooterBgColor || "#1565c0";
  const secondaryColor = brandSettings.Secondary;
  const classes = useStyles({ brandColor, secondaryColor });

  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  
  const lenOk = pwd.length >= 8 && pwd.length <= 50;
  const upperOk = /[A-Z]/.test(pwd);
  const lowerOk = /[a-z]/.test(pwd);
  const numOk = /\d/.test(pwd);
  const specialOk = /[@$!%*?&]/.test(pwd);
  const matchOk = pwd.length > 0 && pwd === confirm;

  const allOk = lenOk && upperOk && lowerOk && numOk && specialOk && matchOk && acceptedTerms;
  const webAssetsBaseUrlSettings = useStoreState((s) => s.siteSettingsModel.webAssetsBaseUrlSettings);

  useEffect(() => {
    if (typeof onValidityChange === "function") {
      onValidityChange(allOk);
      userService.setUser({ password: pwd });
    }
  }, [allOk, onValidityChange]);

  return (
    <Box className={classes.container}>
      <TextField
        variant="outlined"
        placeholder="Create Password"
        type={showPwd ? "text" : "password"}
        value={pwd}
        onFocus={() => adobe.event("createPassword")}
        onChange={(e) => setPwd(e.target.value)}
        className={classes.textField}
        inputProps={{
          "aria-label": "Create Password",
          autoComplete: "new-password",
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LockOutlinedIcon className={classes.lockIcon} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label={
                  showPwd ? "Hide password" : "Show password"
                }
                onClick={() => setShowPwd((value) => !value)}
                edge="end"
                className={classes.visibilityButton}
              >
                {showPwd ? (
                  <VisibilityOff className={classes.visibilityIcon} />
                ) : (
                  <Visibility className={classes.visibilityIcon} />
                )}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Box mt={1} style={{ width: "100%" }}>
        <TextField
          variant="outlined"
          placeholder="Confirm Password"
          type={showConfirm ? "text" : "password"}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          onPaste={(e) => e.preventDefault()}
          onCopy={(e) => e.preventDefault()}
          onCut={(e) => e.preventDefault()}
          onDrop={(e) => e.preventDefault()}
          onDragOver={(e) => e.preventDefault()}
          className={classes.textField}
          inputProps={{
            "aria-label": "Confirm Password",
            autoComplete: "new-password",
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlinedIcon className={classes.lockIcon} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={
                    showConfirm
                      ? "Hide confirm password"
                      : "Show confirm password"
                  }
                  onClick={() =>
                    setShowConfirm((value) => !value)
                  }
                  edge="end"
                  className={classes.visibilityButton}
                >
                  {showConfirm ? (
                    <VisibilityOff
                      className={classes.visibilityIcon}
                    />
                  ) : (
                    <Visibility
                      className={classes.visibilityIcon}
                    />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box className={classes.rulesWrap}>
        Your password must include
        <Rule ok={lenOk} classes={classes}>
          8 - 50 characters long
        </Rule>

        <Rule ok={upperOk} classes={classes}>
          Uppercase letter
        </Rule>

        <Rule ok={lowerOk} classes={classes}>
          Lowercase letter
        </Rule>

        <Rule ok={specialOk} classes={classes}>
          Special characters @$!%*?& only
        </Rule>

        <Rule ok={numOk} classes={classes}>
          Number
        </Rule>

        <Rule ok={matchOk} classes={classes}>
          Passwords match
        </Rule>
      </Box>

      <Box className={classes.termsContainer}>
        <FormControlLabel
          control={
            <Checkbox
              checked={acceptedTerms}
              onFocus={() => adobe.event("termsOfUse")}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              color="primary"
              className={classes.checkbox}
            />
          }
          label={
            <Typography style={{
              fontSize: 12,
              lineHeight: 1.4,
              marginTop: "10px",
              pointerEvents: "none",
            }}>
              I have read and agree to the{" "}
              <Link
                target="_blank"
                rel="noopener noreferrer"
                className={classes.termsLink}
                onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    window.open(webAssetsBaseUrlSettings.Cdn + `pdf/terms/${brandSettings.ProductName === PRODUCT.Grand.bank ? 'Online_Terms_of_Service_GBS' : 'eCS_Registration_terms'}.pdf` , "_blank", "noopener,noreferrer");
                }}
                style={{color: secondaryColor, pointerEvents: "auto"}}
              >
                Terms and Conditions 
              </Link>
              {" "}of Use
            </Typography>
          }
        />
      </Box>
    </Box>
  );
}

function Rule({ ok, classes, children }) {
  const color = ok ? "#00B232" : "#FF0000";
  const Icon = ok ? CheckIcon : CloseIcon;
  return (
    <div className={classes.ruleRow} style={{ color }}>
      <Icon style={{ color, fontSize: 10, marginBottom: 1, marginRight: 8, }} />
      <Typography style={{
        color,
        fontSize: "12px",
        lineHeight: "20px",
        fontWeight: 100,
        margin: 0,
      }}>
        {children}
      </Typography>
    </div>
  );
}
